import axios from 'axios'

import { ApiError } from '@/types/api'

export type FailureLevel = 'error' | 'warning' | 'info'

/** 원인 쪽: server = API 응답, network = 단말·망, web = 우리 코드 */
export type FailureOwner = 'server' | 'network' | 'web'

export interface ApiFailure {
  level: FailureLevel
  owner: FailureOwner
  status?: number
  code?: string
}

const TOKEN_EXPIRED_STATUS = 401
const SERVER_ERROR_MIN_STATUS = 500
const CLIENT_ERROR_MIN_STATUS = 400

/**
 * React Query 실패를 종류별 Sentry 레벨로 나눈다. 알림 규칙은 error 이상만 받는다.
 * - 5xx → error (실제 장애)
 * - 401 → warning (apis/index.ts 가 토큰 재발급 1회 재시도 후에도 실패한 경우만 여기 온다)
 * - 그 밖의 4xx → info (업무 규칙 거부 — 유저에게 안내되는 응답. 주간 리뷰에서 추세만 본다)
 * - 타임아웃·네트워크 끊김 → warning
 * - 그 외 → error
 */
export function classifyApiFailure(error: unknown): ApiFailure {
  if (error instanceof ApiError) return byStatus(error.status, error.code)
  if (axios.isAxiosError(error)) {
    if (error.response) return byStatus(error.response.status)
    return { level: 'warning', owner: 'network', code: error.code }
  }
  return { level: 'error', owner: 'web' }
}

function byStatus(status: number, code?: string): ApiFailure {
  if (status >= SERVER_ERROR_MIN_STATUS) return { level: 'error', owner: 'server', status, code }
  if (status === TOKEN_EXPIRED_STATUS) return { level: 'warning', owner: 'server', status, code }
  if (status >= CLIENT_ERROR_MIN_STATUS) return { level: 'info', owner: 'server', status, code }
  return { level: 'error', owner: 'web', status, code }
}
